"use client";

import React from 'react';
import { useTranslations } from 'next-intl';

interface TaglineBannerSectionProps {
    contentKey?: 'trusted' | 'vision' | 'at_ciscode';
    showBackground?: boolean;
    showQuotes?: boolean;
    customPadding?: string;
}

export const TaglineBannerSection: React.FC<TaglineBannerSectionProps> = ({
    contentKey = 'trusted',
    showBackground = true,
    showQuotes = false,
    customPadding = 'py-4 md:py-8'
}) => {
    const t = useTranslations('TaglineBanner');

    return (
        <section className={`relative w-full ${customPadding} overflow-hidden`}>
            {/* Background with SVG */}
            <div className="absolute inset-0 bg-[#05092B]">
                {/* Conditional SVG Background Image */}
                {showBackground && (
                    <div
                        className="absolute inset-0 bg-center bg-cover opacity-30"
                        style={{
                            backgroundImage: 'url(/svg/bg_reviewsv2.svg)',
                            backgroundPosition: 'center',
                            backgroundSize: 'cover',
                            backgroundRepeat: 'no-repeat'
                        }}
                    ></div>
                )}

                {/* Optional overlay for better text readability */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#05092B]/50 to-[#05092B]/80"></div>
            </div>

            {/* Content */}
            <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 relative z-10">
                <div className="flex items-center justify-center">
                    <div className="text-center relative">
                        {/* Opening Quote with Gradient */}
                        {showQuotes && (
                            <div className="absolute -top-4 -left-2 sm:-top-5 sm:-left-3 md:-top-6 md:-left-8 lg:-top-8 lg:-left-12 opacity-70">
                                <svg width="32" height="32" viewBox="0 0 48 48" className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-16 lg:h-16">
                                    <defs>
                                        <linearGradient id="quoteGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" style={{ stopColor: '#3b82f6', stopOpacity: 1 }} />
                                            <stop offset="100%" style={{ stopColor: '#8b5cf6', stopOpacity: 1 }} />
                                        </linearGradient>
                                    </defs>
                                    <path d="M12 34h6l4-8V14H10v12h6zm16 0h6l4-8V14H26v12h6z" fill="url(#quoteGradient1)" />
                                </svg>
                            </div>
                        )}

                        {/* Main tagline */}
                        <h2 className="text-white text-[45px] not-italic font-medium leading-[52px] relative tracking-wide">
                            <span className="inline-block transition-all duration-300 hover:scale-105 text-white">
                                {t(contentKey)}
                            </span>
                        </h2>

                        {/* Closing Quote with Gradient */}
                        {showQuotes && (
                            <div className="absolute -bottom-4 -right-2 sm:-bottom-5 sm:-right-3 md:-bottom-6 md:-right-8 lg:-bottom-8 lg:-right-12 opacity-70 rotate-180">
                                <svg width="32" height="32" viewBox="0 0 48 48" className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-16 lg:h-16">
                                    <defs>
                                        <linearGradient id="quoteGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" style={{ stopColor: '#8b5cf6', stopOpacity: 1 }} />
                                            <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 1 }} />
                                        </linearGradient>
                                    </defs>
                                    <path d="M12 34h6l4-8V14H10v12h6zm16 0h6l4-8V14H26v12h6z" fill="url(#quoteGradient2)" />
                                </svg>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};